import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Tooltip, Typography } from 'antd';

import TickIcon from '../../../../../resources/icons/versionHistory/TickIcon';

import { getNumberRoundedToHundreds } from '../../../../../utils/helpers/getNumberRoundedToHundreds';
import { getNumberWithCommaAsThousandSeparator } from '../../../../../utils/helpers/getNumberWithCommaAsThousandSeparator';
import {
    calculateOptimalRankabilityScore,
    getOptimalReadabilityLabel,
    getReadabilityItem,
    getReadabilityLabel,
} from '../../../../../utils/helpers/getOptimizerBasicData';
import { openBasicErrorNotification } from '../../../../../utils/helpers/openBasicErrorNotification';
import {
    getThemeColorClass,
    getTooltipOverlayClass,
} from '../../../../../utils/helpers/themeHelpers';
import { useOrganizationSlug } from '../../../../../utils/hooks/useOrganizationSlug';
import {
    getCurrentVersion,
    getVersionBasicData,
} from '../../../../common/tiptapEditor/utils/versionHelpers';

import { rankabilityScoreCategories } from '../../../../../constants/rankabilityScoreCategories';
import { statusCodes } from '../../../../../constants/statusCodes';

import { editorTypes } from '../../../../common/tiptapEditor/constants/tiptapEditorConstants';

import ContentOptimizerService from '../../../../../services/contentOptimizer.service';

import CompleteBadge from '../../../../common/completeBadge/CompleteBadge';
import TableInfoModal from '../../../../common/tableInfo/TableInfoModal';

import { selectDarkMode } from '../../../../../userBrowserSettings/store/browserSettings.selectors';
import { updateEditorBasicData } from '../../../../common/tiptapEditor/store/tiptapEditor.actions';
import { selectEditorBasicData } from '../../../../common/tiptapEditor/store/tiptapEditor.selectors';
import { selectContentOptimizerTabKey } from '../../mainPage/store/contentOptimizerMain.selectors';

import { ContentOptimizerContext } from '../../../../hocs/ContentOptimizerProvider';

const classes = {
    contentOptimizerBasicData: 'content-optimizer-basic-data',
    basicDataBlock: 'basic-data-block',
    blockTitle: 'block-title text-decoration-dotted',
    blockBody: 'block-body',
    checkOutlinedIcon: 'check-outlined-icon',
    textDotted: 'text-decoration-dotted',
    basicDataValue: 'basic-data-value',
    recommendationArticle: 'basic-data-recommendation-article',
};

const ContentOptimizerBasicData = () => {
    const dispatch = useDispatch();

    const contentOptimizerId = useContext(ContentOptimizerContext);

    const darkMode = useSelector(selectDarkMode);
    const basicData = useSelector(selectEditorBasicData);
    const currentTabKey = useSelector(selectContentOptimizerTabKey);

    const organizationSlug = useOrganizationSlug();

    const themeColorClass = getThemeColorClass(darkMode);
    const tooltipOverlayClass = getTooltipOverlayClass(darkMode);

    useEffect(() => {
        const editorType = editorTypes.OPTIMIZER;

        /**
         * Fetch the current version of the optimizer for ONLY updating the basic data.
         * If the current tab is editorTypes.OPTIMIZER we use
         * OTHER request outside for filling out all data including the basic data.
         */
        if (contentOptimizerId && currentTabKey !== editorType && organizationSlug?.length > 0) {
            ContentOptimizerService.getContentOptimizerVersions(contentOptimizerId, {
                slug: organizationSlug,
                active: true,
            })
                .then((result) => {
                    if (result?.status === statusCodes.success && result.data?.length > 0) {
                        const currentVersion = getCurrentVersion(result.data);
                        dispatch(updateEditorBasicData(getVersionBasicData(currentVersion)));
                    }
                })
                .catch(() => {
                    openBasicErrorNotification();
                });
        }
    }, [contentOptimizerId, currentTabKey, organizationSlug, dispatch]);

    const optimalRS = calculateOptimalRankabilityScore(basicData?.optimal_rankability_score?.max);

    const checkReadability = () => {
        const basicDataReadability = Math.max(1, Math.min(basicData?.readability || 100, 100));

        if (basicDataReadability && basicData?.optimal_readability) {
            if (basicData?.optimal_readability?.min && basicData?.optimal_readability?.max) {
                return (
                    basicDataReadability >=
                        getReadabilityItem(basicData?.optimal_readability.min)?.min &&
                    basicDataReadability <=
                        getReadabilityItem(basicData?.optimal_readability.max)?.max
                );
            } else if (basicData?.optimal_readability?.min) {
                return (
                    basicDataReadability >=
                    getReadabilityItem(basicData?.optimal_readability.min)?.min
                );
            } else if (basicData?.optimal_readability?.max) {
                return (
                    basicDataReadability <=
                    getReadabilityItem(basicData?.optimal_readability.max)?.max
                );
            }
        } else {
            return false;
        }
    };

    const checkRankabilityScore = () => {
        if (basicData?.rankability_score && basicData?.optimal_rankability_score) {
            return Math.ceil(basicData?.rankability_score) >= optimalRS;
        } else {
            return false;
        }
    };

    if (!basicData) return null;

    return (
        <div className={`${classes.contentOptimizerBasicData}`}>
            <div className={`${classes.basicDataBlock}`}>
                <Typography className={`d-flex ${classes.blockTitle} ${themeColorClass}`}>
                    <Tooltip
                        placement='bottom'
                        title={
                            'Measures content quality and relevance using natural language processing (NLP) AI technology from Google and IBM Watson.'
                        }
                        overlayClassName={tooltipOverlayClass}
                    >
                        <div className={classes.textDotted}>Rankability Score</div>
                    </Tooltip>
                    <TableInfoModal
                        iconSize='0.8em'
                        title={'Rankability Score'}
                        infoCategories={rankabilityScoreCategories}
                    />
                </Typography>
                <div className={`${classes.basicDataValue} d-flex`}>
                    <CompleteBadge
                        progress={basicData ? Math.ceil(basicData?.rankability_score) : 0}
                        size='small'
                    />
                    {checkRankabilityScore() && (
                        <div className={classes.checkOutlinedIcon}>
                            <TickIcon />
                        </div>
                    )}
                </div>
                <Typography className={classes.recommendationArticle}>
                    Optimal: {optimalRS}+{' '}
                </Typography>
            </div>
            <div className={`${classes.basicDataBlock}`}>
                <Tooltip
                    placement='bottom'
                    title={
                        'Recommended number of words to use in your content relative to the top-ranked competition.'
                    }
                    overlayClassName={tooltipOverlayClass}
                >
                    <Typography className={`${classes.blockTitle} ${themeColorClass}`}>
                        Word Count
                    </Typography>
                </Tooltip>
                <Typography className={`${classes.blockBody}`}>
                    {getNumberWithCommaAsThousandSeparator(basicData?.word_count)}
                    {basicData?.word_count &&
                    basicData?.word_count >=
                        getNumberRoundedToHundreds(basicData?.optimal_word_count.min) &&
                    basicData?.word_count <=
                        getNumberRoundedToHundreds(basicData?.optimal_word_count.max) ? (
                        <div className={classes.checkOutlinedIcon}>
                            <TickIcon />
                        </div>
                    ) : (
                        ''
                    )}
                </Typography>
                <Typography className={classes.recommendationArticle}>
                    Optimal:{' '}
                    {getNumberWithCommaAsThousandSeparator(
                        getNumberRoundedToHundreds(basicData?.optimal_word_count.min)
                    )}
                    {' - '}
                    {getNumberWithCommaAsThousandSeparator(
                        getNumberRoundedToHundreds(basicData?.optimal_word_count.max)
                    )}
                </Typography>
            </div>
            <div className={`${classes.basicDataBlock}`}>
                <Tooltip
                    placement='bottom'
                    title={
                        'Grade that measures how easy your content is to read based on the Flesch-Kincaid formula.'
                    }
                    overlayClassName={tooltipOverlayClass}
                >
                    <Typography className={`${classes.blockTitle} ${themeColorClass}`}>
                        Readability
                    </Typography>
                </Tooltip>
                <Typography className={`${classes.blockBody}`}>
                    {getReadabilityLabel(basicData?.readability || 100)}
                    {checkReadability() && (
                        <div className={classes.checkOutlinedIcon}>
                            <TickIcon />
                        </div>
                    )}
                </Typography>
                <Typography className={classes.recommendationArticle}>
                    Optimal: {getOptimalReadabilityLabel(basicData?.optimal_readability)}
                </Typography>
            </div>
        </div>
    );
};

export default ContentOptimizerBasicData;
